import axios from 'axios';

/**
 * "Skicka till väghållaren" (HK-1117): hand a work order to the road owner
 * instead of picking contractors by hand. The API routes the order to the
 * road-authority contractor whose filterset matches each stop, in practice
 * the Trafikverket maintenance district written to this property by the
 * district import (mobilix-api, HK-1113).
 */
export const MAINTENANCE_DISTRICT_KEY = 'meta.maintenance_district';

export const isRoadAuthority = (contractor: Contractor): boolean =>
  contractor.kind === 'road_authority';

interface ContractorsByKind {
  /** Ordinary contractors, the ones offered in the contractor dropdown. */
  contractors: Contractor[];
  /** Road owners (Trafikverket districts); only reachable via the handoff. */
  roadAuthorities: Contractor[];
}

export const splitContractorsByKind = (
  contractors: Contractor[],
): ContractorsByKind => ({
  contractors: contractors.filter((c) => !isRoadAuthority(c)),
  roadAuthorities: contractors.filter(isRoadAuthority),
});

/** The stop's maintenance district, or undefined when unset or blank. */
export const districtOf = (entity: Entity): string | undefined => {
  const value = entity.properties[MAINTENANCE_DISTRICT_KEY];
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

export interface HandoffRow {
  entity: Entity;
  district?: string;
}

export type HandoffBlocker =
  /** The choice is on but no stop is selected. Sending anyway would make the
   *  API create one order per road-authority contractor, with no stop. */
  | { reason: 'no_entities' }
  /** At least one selected stop has no district, so the API's filterset match
   *  cannot succeed for it. Listed so the UI can point at the stops. */
  | { reason: 'missing_district'; entities: Entity[] }
  /** Selected ids the stop list does not know, so their district cannot be
   *  checked. Not sent unchecked. */
  | { reason: 'unknown_entities'; count: number };

export interface HandoffSummary {
  rows: HandoffRow[];
  blocker?: HandoffBlocker;
}

/** What the user sees before submitting: each selected stop with its
 *  district, and why (if) the handoff cannot be sent yet. The district is a
 *  preview only; the API's 422 is the authoritative check. */
export const summarizeHandoff = (
  selected: Entity[],
  unknownCount = 0,
): HandoffSummary => {
  const rows = selected.map((entity) => ({
    entity,
    district: districtOf(entity),
  }));
  if (rows.length === 0 && unknownCount === 0) {
    return { rows, blocker: { reason: 'no_entities' } };
  }
  if (unknownCount > 0) {
    return {
      rows,
      blocker: { reason: 'unknown_entities', count: unknownCount },
    };
  }
  const missing = rows
    .filter((row) => row.district === undefined)
    .map((row) => row.entity);
  return missing.length > 0
    ? { rows, blocker: { reason: 'missing_district', entities: missing } }
    : { rows };
};

/**
 * The ids from a `POST /workorders?auto_assign=true&require_match=true` 422
 * (`ApiWorkOrderUnmatchedEntitiesError`): stops that matched no road-authority
 * filterset, so nothing was created. Null for any other error, including a
 * 422 that names no stop, so the caller falls back to the generic handling.
 */
export const unmatchedEntitiesFromError = (error: unknown): string[] | null => {
  if (!axios.isAxiosError(error) || error.response?.status !== 422) {
    return null;
  }
  const data: unknown = error.response.data;
  if (typeof data !== 'object' || data === null) {
    return null;
  }
  const unmatched = (data as { unmatched_entities?: unknown })
    .unmatched_entities;
  return Array.isArray(unmatched) &&
    unmatched.length > 0 &&
    unmatched.every((id) => typeof id === 'string')
    ? unmatched
    : null;
};

/**
 * True for exactly the error the create work order dialog explains inline:
 * a 422 with unmatched stops from `POST /workorders` sent with
 * `require_match`, i.e. the road-authority handoff. Any other 422, and the
 * same body shape on another request, is left to the generic error handling.
 */
export const isRoadAuthorityUnmatchedError = (error: unknown): boolean => {
  if (
    !axios.isAxiosError(error) ||
    unmatchedEntitiesFromError(error) === null
  ) {
    return false;
  }
  const config = error.config;
  const params = config?.params as { require_match?: unknown } | undefined;
  return (
    config?.method?.toLowerCase() === 'post' &&
    config.url?.endsWith('/workorders') === true &&
    params?.require_match === true
  );
};
